/**
 * Hashes an email address using SHA-256 for Google Enhanced Conversions.
 * Normalizes the email by trimming whitespace and converting to lowercase before hashing.
 * Works in both browser (Web Crypto API) and Node.js environments.
 *
 * @param email - The email address to hash
 * @returns A promise that resolves to the SHA-256 hash as a lowercase hex string
 */
export const hashEmail = async (email: string): Promise<string> => {
  const normalized = email.toLowerCase().trim()

  // Use Web Crypto API in browser, Node.js crypto module in Node/Jest
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const encoder = new TextEncoder()
    const data = encoder.encode(normalized)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)

    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
  }

  // Node.js fallback
  const {createHash} = await import('crypto')
  return createHash('sha256').update(normalized).digest('hex')
}
